import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '@/state'

import { debounce } from 'lodash'
import { setFormModeAction } from '@/state/ui/global/slices/index.slices'
import {
  ReportsFilters,
  GetReportsPayload,
  RoundsReportLibraryColumnProps,
  RoundsReportData,
  GlobalReportLibraryColumnProps,
  GlobalReportData,
} from '@/state/reports/models'

import { ReportsActions } from '@/state/reports/slices/index.slices'
import { getAllCategoriesThunk } from '@/state/wagering-contribution/thunks/get-all-categories.thunk'
import { getAllGamesThunk } from '@/state/wagering-contribution/thunks/get-all-games.thunk'

import { getGlobalReportsThunk } from '@/state/reports/thunks/get-global.thunk'
import { getRoundsReportsThunk } from '@/state/reports/thunks/get-rounds.thunk'
import { getProvidersThunk } from '@/state/bonus-engine/thunks/get-providers.thunk'

import { TableHeadCellPropsType } from '@/state/ui/global/models'
import { exportXlsxFile } from '@/utils'
import { FormStatusType } from '@/enums/form-status.enum'
import { ReportsType } from '@/enums'

const useReportsActions = () => {
  const dispatch = useAppDispatch()

  const { t } = useTranslation()

  const getRoundsReports = async (
    filters: GetReportsPayload,
    headCells?: TableHeadCellPropsType<RoundsReportLibraryColumnProps>[],
    fileName?: string,
    getFormattedRow?: (
      row: RoundsReportData | GlobalReportData,
    ) => RoundsReportLibraryColumnProps | GlobalReportLibraryColumnProps,
  ) => {
    const resultAction = await dispatch(
      getRoundsReportsThunk({
        ...filters,
      }),
    )

    if (
      getFormattedRow &&
      headCells &&
      getRoundsReportsThunk.fulfilled.match(resultAction)
    ) {
      exportXlsxFile(
        headCells,
        resultAction.payload.docs.map((item) => getFormattedRow(item)),
        fileName,
      )
    }
  }

  const sumGlobalReportRows = (
    rows: GlobalReportData[],
    filters: GetReportsPayload,
  ) => {
    let summaryData = {
      totalRoundsPlayers: rows[0].totalRoundsPlayers,
      totalBetsPlayers: rows[0].totalBetsPlayers,
      totalWinsPlayers: rows[0].totalWinsPlayers,
      totalgameWinPlayers: rows[0].totalgameWinPlayers,
      totalRtpPlayers: rows[0].totalRtpPlayers,
      currency: rows[0].currency,
    }

    return summaryData
  }

  const getGlobalReports = async (
    filters: GetReportsPayload,
    headCells?: TableHeadCellPropsType<GlobalReportLibraryColumnProps>[],
    fileName?: string,
    getFormattedRow?: (
      row: RoundsReportData | GlobalReportData,
    ) => RoundsReportLibraryColumnProps | GlobalReportLibraryColumnProps,
  ) => {
    const resultAction = await dispatch(
      getGlobalReportsThunk({
        ...filters,
      }),
    )

    if (
      getFormattedRow &&
      headCells &&
      getGlobalReportsThunk.fulfilled.match(resultAction)
    ) {
      const { reportType } = filters
      const mappedDocs = resultAction.payload.docs.map((doc: any) => {
        let name = doc.name
        if (reportType === ReportsType.BY_CATEGORY) name = doc.gameCategoryName
        if (reportType === ReportsType.BY_PROVIDER) name = doc.gameProviderName
        if (reportType === ReportsType.BY_GAME) name = doc.gameName
        const { gameCategoryName, gameProviderName, gameName, ...rest } = doc
        return { ...rest, name }
      })

      const summary = sumGlobalReportRows(resultAction.payload.docs, filters)
      exportXlsxFile(
        headCells,
        [
          ...mappedDocs.map((item) => getFormattedRow(item)),
          {
            name: t('labels.total'),
            playerId: t('labels.total'),
            rounds: Number(summary.totalRoundsPlayers.toString()),
            bets: Number(summary.totalBetsPlayers),
            totalBets: Number(summary.totalBetsPlayers),
            wins: Number(summary.totalWinsPlayers),
            totalWins: Number(summary.totalWinsPlayers),
            gameWin: Number(summary.totalgameWinPlayers),
            rtp: Number(
              Number(
                (Number(summary.totalWinsPlayers) /
                  Number(summary.totalBetsPlayers)) *
                  100,
              ).toFixed(2),
            ),
            currency: summary.currency,
          },
        ],
        fileName,
      )
    }
  }

  const getAllGames = debounce(
    (hideFullLoading?: boolean) => {
      dispatch(getAllGamesThunk({ hideFullLoading }))
    },
    500, // eslint-disable-line
  )

  const getAllCategories = debounce(
    (hideFullLoading?: boolean) => {
      dispatch(getAllCategoriesThunk({ hideFullLoading }))
    },
    500, // eslint-disable-line
  )

  const getAllProviders = debounce(
    (hideFullLoading?: boolean) => {
      dispatch(getProvidersThunk({ hideFullLoading }))
    },
    500, // eslint-disable-line
  )

  const setFormType = useCallback(
    (formMode: FormStatusType) => dispatch(setFormModeAction(formMode)),
    [dispatch],
  )

  const setReportsFilters = useCallback(
    (filters: ReportsFilters) =>
      dispatch(ReportsActions.setReportsFiltersAction(filters)),
    [dispatch],
  )

  const resetGlobalReports = useCallback(
    () => dispatch(ReportsActions.resetGlobalReportsAction()),
    [dispatch],
  )

  const resetRoundsReports = useCallback(
    () => dispatch(ReportsActions.resetRoundsReportsAction()),
    [dispatch],
  )

  return {
    getGlobalReports,
    getRoundsReports,
    getAllGames,
    getAllCategories,
    getAllProviders,
    setFormType,
    setReportsFilters,
    resetGlobalReports,
    resetRoundsReports,
  }
}

export default useReportsActions
